import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Activity, Search, ChevronLeft, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCION_LABELS = {
  crear: 'Crear', editar: 'Editar', eliminar: 'Eliminar',
  login: 'Login', logout: 'Logout', exportar: 'Exportar', cambio_permiso: 'Cambio Permiso'
};

const ACCION_COLORS = {
  crear: 'bg-green-100 text-green-800',
  editar: 'bg-blue-100 text-blue-800',
  eliminar: 'bg-red-100 text-red-800',
  login: 'bg-purple-100 text-purple-800',
  logout: 'bg-gray-100 text-gray-800',
  exportar: 'bg-yellow-100 text-yellow-800',
};

export default function Audit() {
  const { hasPermission } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({ modulo: '', accion: '' });
  const limit = 25;

  useEffect(() => { fetchLogs(); }, [page, filters]);

  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams({ skip: page * limit, limit });
      if (filters.modulo) params.append('modulo', filters.modulo);
      if (filters.accion) params.append('accion', filters.accion);
      const res = await axios.get(`${API}/audit?${params}`);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (err) { console.error(err); }
  };

  if (!hasPermission('auditoria', 'ver')) {
    return <div className="text-center py-20 text-stone-500" data-testid="access-denied">Acceso denegado</div>;
  }

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Auditoría del Sistema</h1>
          <p className="text-stone-500 text-sm mt-1">{total} registros totales</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap gap-3">
            <Select value={filters.modulo} onValueChange={v => { setFilters(f => ({...f, modulo: v === 'all' ? '' : v})); setPage(0); }}>
              <SelectTrigger className="w-[180px]" data-testid="audit-filter-modulo">
                <SelectValue placeholder="Todos los módulos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los módulos</SelectItem>
                {['usuarios', 'roles', 'eventos', 'finanzas', 'inventario', 'documentos', 'anuncios', 'grupos', 'encuestas', 'configuracion', 'campos_dinamicos', 'auth'].map(m => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.accion} onValueChange={v => { setFilters(f => ({...f, accion: v === 'all' ? '' : v})); setPage(0); }}>
              <SelectTrigger className="w-[180px]" data-testid="audit-filter-accion">
                <SelectValue placeholder="Todas las acciones" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las acciones</SelectItem>
                {Object.keys(ACCION_LABELS).map(a => (
                  <SelectItem key={a} value={a}>{ACCION_LABELS[a]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-stone-500">
                  <th className="pb-3 font-medium">Fecha</th>
                  <th className="pb-3 font-medium">Usuario</th>
                  <th className="pb-3 font-medium">Acción</th>
                  <th className="pb-3 font-medium">Módulo</th>
                  <th className="pb-3 font-medium">Registro</th>
                  <th className="pb-3 font-medium">IP</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <tr key={i} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`audit-row-${i}`}>
                    <td className="py-3 text-xs text-stone-500">
                      {log.timestamp ? new Date(log.timestamp).toLocaleString('es-ES') : '-'}
                    </td>
                    <td className="py-3">
                      <div className="font-medium text-stone-700">{log.user_name}</div>
                      <div className="text-xs text-stone-400">{log.user_email}</div>
                    </td>
                    <td className="py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${ACCION_COLORS[log.accion] || 'bg-stone-100 text-stone-800'}`}>
                        {ACCION_LABELS[log.accion] || log.accion}
                      </span>
                    </td>
                    <td className="py-3 text-stone-600 capitalize">{log.modulo}</td>
                    <td className="py-3 text-xs text-stone-400 font-mono">{log.registro_id ? log.registro_id.slice(0, 8) + '...' : '-'}</td>
                    <td className="py-3 text-xs text-stone-400">{log.ip || '-'}</td>
                  </tr>
                ))}
                {logs.length === 0 && (
                  <tr><td colSpan={6} className="py-8 text-center text-stone-400">No hay registros de auditoría</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <span className="text-sm text-stone-500">Página {page + 1} de {totalPages}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="audit-prev">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="audit-next">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
